// Small shared pieces of the planning screens: the offline flag, the facility's
// current week and the quantity and percentage boxes.

import { useSyncExternalStore } from "react";
import { onlineManager } from "@tanstack/react-query";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { addDaysIso, isoWeekNumber, mondayOfIso } from "../../../../components/bucket-grid-paste";

function subscribe(onChange: () => void): () => void {
  return onlineManager.subscribe(onChange);
}

/** True while the browser has no network; every planning write waits for it. */
export function useOffline(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => !onlineManager.isOnline(),
    () => false,
  );
}

/** The Monday of the facility's current week, in its own time zone. */
export function currentMonday(
  timezone: string | null | undefined,
  nowMs: number = Date.now(),
): string {
  return mondayOfIso(facilityTodayIso(nowMs, timezone));
}

/** The default window: from this Monday across the facility's horizon (spec 03 §4.3). */
export function defaultWindow(monday: string, horizonWeeks: number): { from: string; to: string } {
  return { from: monday, to: addDaysIso(monday, 7 * (Math.max(1, horizonWeeks) - 1)) };
}

const dayMonth = new Map<string, Intl.DateTimeFormat>();

/** A week's column label, "W42 · 12 Oct" in the reader's locale. */
export function weekLabel(monday: string, locale: string): string {
  let fmt = dayMonth.get(locale);
  if (fmt === undefined) {
    fmt = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" });
    dayMonth.set(locale, fmt);
  }
  return `W${String(isoWeekNumber(monday))} · ${fmt.format(new Date(`${monday}T00:00:00Z`))}`;
}

const fullDate = new Map<string, Intl.DateTimeFormat>();

/** A business date in the reader's locale. */
export function formatDate(iso: string, locale: string): string {
  let fmt = fullDate.get(locale);
  if (fmt === undefined) {
    fmt = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
    fullDate.set(locale, fmt);
  }
  return fmt.format(new Date(`${iso}T00:00:00Z`));
}

/** A percentage box value as the API's decimal string, or undefined when it is not one. */
export function parsePct(text: string): string | undefined {
  const raw = text.trim().replace(",", ".");
  if (!/^\d{1,3}(\.\d{1,2})?$/u.test(raw)) return undefined;
  return String(Number(raw));
}

/** A whole number within bounds, or undefined. */
export function parseWhole(text: string, min: number, max: number): number | undefined {
  const raw = text.trim();
  if (!/^\d+$/u.test(raw)) return undefined;
  const n = Number(raw);
  return n >= min && n <= max ? n : undefined;
}
